import React, { useState, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { Modal, Spin } from 'antd'
import { CheckOutlined, QuestionCircleOutlined, ArrowRightOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import {
  COLORS,
  BORDER_RADIUS,
  SPACING,
  TRANSITIONS,
  TYPOGRAPHY,
  FONT_WEIGHT,
} from '../../styles/theme'
import { ProfileService, type TierComparisonData } from '../../services/profile/profileService'

interface UpgradePromptModalProps {
  isOpen: boolean
  onClose: () => void
  featureName?: string
  zIndex?: number
}

const ModalContent = styled.div`
  padding: 8px 0;
`

const Header = styled.div`
  text-align: center;
  margin-bottom: ${SPACING.xl};
`

const Title = styled.h2`
  font-size: ${TYPOGRAPHY.h3.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  margin: 0 0 ${SPACING.sm} 0;
  color: var(--color-text-primary);
  letter-spacing: -0.5px;
`

const Subtitle = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
`

const FeatureHighlight = styled.span`
  background: linear-gradient(135deg, ${COLORS.primary} 0%, #9b7dfc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: ${FONT_WEIGHT.semibold};
`

const TrialHighlight = styled.span`
  color: ${COLORS.primary};
  font-weight: ${FONT_WEIGHT.semibold};
`

// Comparison Table
const ComparisonTable = styled.div`
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.xl};
  overflow: hidden;
  background: var(--color-bg-secondary);
  margin-bottom: ${SPACING.xl};
`

const ComparisonHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: var(--color-bg-elevated);
  border-bottom: 1px solid var(--color-border);
`

const ComparisonHeaderCell = styled.div<{ $highlight?: boolean }>`
  padding: ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${(props) => (props.$highlight ? COLORS.primary : 'var(--color-text-primary)')};
  text-align: center;
  &:first-child {
    text-align: left;
  }
`

const ComparisonRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`

const ComparisonCell = styled.div<{ $highlight?: boolean }>`
  padding: ${SPACING.sm} ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$highlight ? 'rgba(110, 83, 249, 0.05)' : 'transparent')};
  &:first-child {
    justify-content: flex-start;
  }
  .anticon-check {
    color: ${COLORS.primary};
    font-size: ${TYPOGRAPHY.body.fontSize};
  }
`

const PriceRow = styled(ComparisonRow)`
  background: var(--color-bg-elevated);
`

const PriceCell = styled(ComparisonCell)`
  flex-direction: column;
  padding: ${SPACING.md};
  gap: 2px;
  &:first-child {
    align-items: flex-start;
  }
`

const PriceAmount = styled.span`
  font-size: ${TYPOGRAPHY.h3.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  letter-spacing: -1px;
`

const PricePeriod = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
`

const FeatureWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

// Tooltip
const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const TooltipIcon = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  transition: color ${TRANSITIONS.fast};
  width: 14px;
  height: 14px;
  &:hover {
    color: ${COLORS.primary};
  }
  .anticon {
    font-size: ${TYPOGRAPHY.caption.fontSize};
  }
`

const TooltipContent = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.md};
  padding: 10px 12px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.5;
  width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.2s,
    visibility 0.2s;
  z-index: 100;
  pointer-events: none;
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: var(--color-border);
  }
`

function Tooltip({ content }: { content: string }) {
  const [visible, setVisible] = useState(false)
  return (
    <TooltipWrapper onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <TooltipIcon type="button" aria-label="More info">
        <QuestionCircleOutlined />
      </TooltipIcon>
      <TooltipContent $visible={visible}>{content}</TooltipContent>
    </TooltipWrapper>
  )
}

// CTA Row
const CTARow = styled(ComparisonRow)`
  background: var(--color-bg-elevated);
  border-bottom: none;
`

const CTACell = styled(ComparisonCell)`
  padding: ${SPACING.md};
`

const UpgradeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  border-radius: ${BORDER_RADIUS.md};
  background: linear-gradient(135deg, ${COLORS.primary} 0%, #9b7dfc 100%);
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 2px 8px ${COLORS.primaryShadow};
  transition: ${TRANSITIONS.normal};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${COLORS.primaryShadow};
  }

  .anticon {
    font-size: ${TYPOGRAPHY.caption.fontSize};
  }
`

const CurrentPlanButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  border-radius: ${BORDER_RADIUS.md};
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  cursor: pointer;
  transition: ${TRANSITIONS.normal};

  &:hover {
    background: var(--color-bg-tertiary);
    border-color: var(--color-text-tertiary);
  }
`

// Format a value from tier settings for display
function renderValue(value: boolean | number | string | undefined): React.ReactNode {
  if (value === undefined) return '—'
  if (typeof value === 'boolean') return value ? <CheckOutlined /> : '—'
  if (typeof value === 'number') return value === 0 ? 'Unlimited' : value.toLocaleString()
  return value
}

export function UpgradePromptModal({
  isOpen,
  onClose,
  featureName,
  zIndex,
}: UpgradePromptModalProps) {
  const [tierData, setTierData] = useState<TierComparisonData | null>(null)
  const [loading, setLoading] = useState(false)

  // Fetch tier data when modal opens
  useEffect(() => {
    if (isOpen && !tierData) {
      setLoading(true)
      ProfileService.getTierComparison()
        .then((response) => {
          if (response.data) {
            setTierData(response.data)
          }
        })
        .catch((err) => {
          console.error('Failed to fetch tier comparison:', err)
          Sentry.captureException(err, {
            tags: { feature: 'upgrade', action: 'fetch_tier_comparison' },
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [isOpen, tierData])

  // Build comparison rows from DB labels — fully data-driven
  const comparisonRows = useMemo(() => {
    if (!tierData) return []
    const { free, paid } = tierData
    return Object.entries(free.labels ?? {}).map(([key, label]) => ({
      key,
      label,
      freeValue: free[key as keyof typeof free] as boolean | number | string | undefined,
      paidValue: paid[key as keyof typeof paid] as boolean | number | string | undefined,
      tooltip: free.tooltips?.[key],
    }))
  }, [tierData])

  const handleUpgrade = () => {
    // Navigate to upgrade page in same tab for better UX
    window.location.href = '/app/upgrade'
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={780}
      centered
      destroyOnHidden
      zIndex={zIndex}
    >
      <ModalContent>
        <Header>
          <Title>
            {featureName ? (
              <>
                <FeatureHighlight>{featureName}</FeatureHighlight> is a Pro feature
              </>
            ) : (
              'Upgrade to Pro'
            )}
          </Title>
          <Subtitle>
            {tierData && tierData.paid.trial_days > 0 ? (
              <>
                Start with a{' '}
                <TrialHighlight>{tierData.paid.trial_days}-day free trial</TrialHighlight> — no
                charge until it ends.
              </>
            ) : (
              'Compare plans and unlock unlimited projects, exports, and team features.'
            )}
          </Subtitle>
        </Header>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : (
          <ComparisonTable>
            <ComparisonHeader>
              <ComparisonHeaderCell>Feature</ComparisonHeaderCell>
              <ComparisonHeaderCell>Free</ComparisonHeaderCell>
              <ComparisonHeaderCell $highlight>Pro</ComparisonHeaderCell>
            </ComparisonHeader>

            <PriceRow>
              <PriceCell>
                <span
                  style={{ fontWeight: FONT_WEIGHT.semibold, color: 'var(--color-text-primary)' }}
                >
                  Price
                </span>
              </PriceCell>
              <PriceCell>
                <PriceAmount>${tierData?.free.price_monthly ?? 0}</PriceAmount>
                <PricePeriod>{tierData?.free.price_period ?? 'forever'}</PricePeriod>
              </PriceCell>
              <PriceCell $highlight>
                <PriceAmount>${tierData?.paid.price_monthly ?? 20}</PriceAmount>
                <PricePeriod>/ {tierData?.paid.price_period ?? 'month'}</PricePeriod>
              </PriceCell>
            </PriceRow>

            {comparisonRows.map((row) => (
              <ComparisonRow key={row.key}>
                <ComparisonCell>
                  <FeatureWithTooltip>
                    {row.label}
                    {row.tooltip && <Tooltip content={row.tooltip} />}
                  </FeatureWithTooltip>
                </ComparisonCell>
                <ComparisonCell>{renderValue(row.freeValue)}</ComparisonCell>
                <ComparisonCell $highlight>{renderValue(row.paidValue)}</ComparisonCell>
              </ComparisonRow>
            ))}

            <CTARow>
              <CTACell />
              <CTACell>
                <CurrentPlanButton onClick={onClose}>Current Plan</CurrentPlanButton>
              </CTACell>
              <CTACell $highlight>
                <UpgradeButton onClick={handleUpgrade}>
                  {tierData && tierData.paid.trial_days > 0 ? 'Start free trial' : 'Upgrade'}
                  <ArrowRightOutlined />
                </UpgradeButton>
              </CTACell>
            </CTARow>
          </ComparisonTable>
        )}
      </ModalContent>
    </Modal>
  )
}
